import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import { Account, Job, Product, Panel, ProductionScheduleData } from "./types";
import { quotes } from "../../types/api-types/crm/quotes/quotes.types";
import { quoteLineItems as QuoteLineItemType } from "../../types/api-types/crm/quote-line-items/quote-line-items.types";
import {
  salesOrders,
  salesOrdersData,
} from "../../types/api-types/crm/sales-orders/sales-orders.types";
import { salesOrderLineItems as SalesOrderLineItemType } from "../../types/api-types/crm/sales-order-line-items/sales-order-line-items.types";
import { ViewStoreErrorHandler } from "@intrasync/stores/utils";

interface TableStats {
  tableNumber: number;
  tablePanelCount: number;
  gridRow: number;
  gridPosition: number;
  gridSize: number;
}

export default class SalesOrderConversionViewStore {
  rootStore: RootStore;
  errorHandler: ViewStoreErrorHandler;

  // Observables - Step Management
  currentStep = 1;
  isLoading = false;
  error: string | null = null;

  // Observables - Entity IDs
  selectedQuoteId: number | undefined = undefined;
  activeSalesOrderId: number | undefined = undefined;

  // Observables - UI State
  panelTableAssignments: { [key: string]: string } = {};
  scheduleDate: string | null = null;

  // Track panel IDs created in current workflow
  createdPanelIds: number[] = [];

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false, errorHandler: false });
    this.rootStore = rootStore;

    // Initialize standardized error handling
    this.errorHandler = new ViewStoreErrorHandler(
      rootStore,
      "SalesOrderConversionViewStore",
      {
        enableErrorNotifications: true,
        enableReactionBatching: true,
        debugMode: false,
      }
    );
  }

  // Computed Values - Access entities from ServiceStores
  get availableQuotes(): quotes[] {
    const store = this.rootStore.quotesStore as ServiceStore;
    const allQuotes = store?.entities || [];

    console.log("All quotes in store:", allQuotes.length);

    // Filter for quotes that are in a state ready for conversion
    // Also filter out any duplicates by quoteNumber
    const seenQuoteNumbers = new Set<string>();
    const filtered = allQuotes.filter((quote: quotes) => {
      // Check if we've already seen this quote number (duplicate check)
      if (seenQuoteNumbers.has(quote.quoteNumber)) {
        console.log(`Duplicate quote found: ${quote.quoteNumber}, skipping`);
        return false;
      }
      seenQuoteNumbers.add(quote.quoteNumber);

      const isConvertible =
        quote.status === "Accepted" ||
        quote.status === "Approved" ||
        quote.status === "Draft" ||
        quote.status === "Submitted";

      return isConvertible;
    });

    console.log("Available quotes after filtering:", filtered.length);
    return filtered;
  }

  get selectedQuote(): quotes | null {
    if (!this.selectedQuoteId) return null;
    const store = this.rootStore.quotesStore as ServiceStore;
    return (
      store?.entities?.find((q: quotes) => q.id === this.selectedQuoteId) ||
      null
    );
  }

  get quoteLineItems(): QuoteLineItemType[] {
    if (!this.selectedQuoteId) return [];
    const store = this.rootStore.quoteLineItemsStore as ServiceStore;

    // Debug logging
    const allLineItems = store?.entities || [];
    console.log("All quote line items in store:", allLineItems.length, allLineItems);
    console.log(
      "Looking for quoteId:",
      this.selectedQuoteId,
      typeof this.selectedQuoteId
    );

    // Log all line items to see their quoteIds
    allLineItems.forEach((item: QuoteLineItemType, index: number) => {
      console.log(`Line item ${index}: quoteId=${item.quoteId} (type: ${typeof item.quoteId}), description=${item.description}`);
    });

    const filtered = allLineItems.filter((item: QuoteLineItemType) => {
      // Handle both string and number comparisons
      const itemQuoteId = Number(item.quoteId);
      const selectedId = Number(this.selectedQuoteId);

      console.log(`Comparing: itemQuoteId=${itemQuoteId} with selectedId=${selectedId}, match=${itemQuoteId === selectedId}`);

      const matches = itemQuoteId === selectedId;
      if (matches) {
        console.log("Found matching line item:", item);
      }
      return matches;
    });

    console.log("Filtered line items for quote:", filtered.length);
    return filtered;
  }

  get activeSalesOrder(): salesOrders | null {
    if (!this.activeSalesOrderId) return null;
    const store = this.rootStore.salesOrdersStore as ServiceStore;
    return (
      store?.entities?.find(
        (so: salesOrders) => so.id === this.activeSalesOrderId
      ) || null
    );
  }

  get salesOrderLineItems(): SalesOrderLineItemType[] {
    if (!this.activeSalesOrderId) return [];
    const store = this.rootStore.salesOrderLineItemsStore as ServiceStore;
    return (
      store?.entities?.filter(
        (item: SalesOrderLineItemType) =>
          item.salesOrderId === this.activeSalesOrderId
      ) || []
    );
  }

  get generatedPanels(): Panel[] {
    // Get panels from the panel ServiceStore that were created in this workflow
    const panelStore = this.rootStore.panelStore as ServiceStore;
    return (
      panelStore?.entities
        ?.filter((entity: unknown) => {
          const panel = entity as Panel;
          return this.createdPanelIds.includes(panel.id as number);
        })
        .map((entity: unknown) => entity as Panel) || []
    );
  }

  get accounts(): Account[] {
    const store = this.rootStore.accountsStore as ServiceStore;
    return store?.entities?.map((entity: unknown) => entity as Account) || [];
  }

  get projects(): Array<{
    id: number;
    projectName: string;
    accountId?: number;
  }> {
    const store = this.rootStore.jobStore as ServiceStore;
    return (
      store?.entities?.map((entity: unknown) => {
        const job = entity as Job;
        return {
          id: job.id,
          projectName: job.name || "",
          accountId: job.accountId,
        };
      }) || []
    );
  }

  get products(): Product[] {
    const store = this.rootStore.productsStore as ServiceStore;
    return store?.entities?.map((entity: unknown) => entity as Product) || [];
  }

  get tableList(): TableStats[] {
    const castingMapLayoutStore = this.rootStore
      .castingMapLayoutStore as ServiceStore;
    return (
      (castingMapLayoutStore?.entities
        ?.slice()
        ?.filter(
          (table: unknown) =>
            (table as { startDate: string; endDate: string }).startDate <=
              this.castingDisplayDate &&
            (((table as { startDate: string; endDate: string }).endDate !==
              "1970-01-01" &&
              (table as { startDate: string; endDate: string }).endDate >=
                this.castingDisplayDate) ||
              (table as { startDate: string; endDate: string }).endDate ===
                "1970-01-01")
        ) as unknown as TableStats[]) || []
    );
  }

  get availableTablesFromList() {
    const uniqueTableNumbers = [
      ...new Set(this.tableList.map((i: TableStats) => String(i.tableNumber))),
    ];
    return uniqueTableNumbers.sort((a, b) => Number(a) - Number(b));
  }

  get castingDisplayDate(): string {
    return this.scheduleDate || new Date().toISOString().split("T")[0];
  }

  get workflowProgress(): number {
    return ((this.currentStep - 1) / 3) * 100;
  }

  // Actions - UI State Management
  setCurrentStep = (step: number) => {
    this.currentStep = step;
  };

  setSelectedQuote = (quoteId: number) => {
    this.selectedQuoteId = quoteId;
  };

  setError = (_operation: string, error: string) => {
    this.error = error;
  };

  clearError = () => {
    this.error = null;
  };

  setPanelTableAssignment = (panelId: string, tableNumber: string) => {
    this.panelTableAssignments[panelId] = tableNumber;
  };

  getPanelTableAssignment = (panelId: string): string => {
    return this.panelTableAssignments[panelId] || "";
  };

  reset = () => {
    this.currentStep = 1;
    this.selectedQuoteId = undefined;
    this.activeSalesOrderId = undefined;
    this.createdPanelIds = [];
    this.panelTableAssignments = {};
    this.scheduleDate = null;
    this.error = null;
    this.isLoading = false;
  };

  // Async Actions
  loadQuotes = async () => {
    this.isLoading = true;

    try {
      const quotesStore = this.rootStore.quotesStore as ServiceStore;
      const quoteLineItemsStore = this.rootStore
        .quoteLineItemsStore as ServiceStore;

      // Load quotes with filter to get convertible quotes
      // Use empty query to force fresh fetch from backend
      await Promise.all([
        quotesStore.find({ query: {} }),
        quoteLineItemsStore.find({ query: {} }),
      ]);
    } catch (error) {
      runInAction(() => {
        this.error = (error as Error).message || "Failed to load quotes";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  convertQuoteToSalesOrder = async (quoteId: number) => {
    this.isLoading = true;
    this.error = null;

    try {
      const quotesStore = this.rootStore.quotesStore as ServiceStore;
      const quote = quotesStore?.entities?.find(
        (q: quotes) => q.id === quoteId
      );
      if (!quote) {
        throw new Error("Quote not found");
      }

      // Generate sales order number
      const timestamp = Date.now();
      const soNumber = `SO-${new Date().getFullYear()}-${String(
        timestamp
      ).slice(-6)}`;

      // Create sales order from quote using API types
      const newSalesOrder: salesOrdersData = {
        soNumber,
        quoteId: quote.id,
        accountId: quote.accountId,
        jobId: quote.jobId,
        salesPersonId: quote.salesPersonId,
        orderDate: new Date().toISOString(),
        subtotalAmount: quote.subtotalAmount,
        totalAmount: quote.totalAmount || 0,
        currency: quote.currency,
        status: "Approved",
        priorityLevel: "Medium",
        paymentTerms: quote.paymentTerms ? `${quote.paymentTerms}` : "Net 30",
        notes: quote.notes || "",
      };

      // Create the sales order using the service store
      const salesOrdersStore = this.rootStore.salesOrdersStore as ServiceStore;
      const createdSO = await salesOrdersStore.create(newSalesOrder);

      if (!createdSO) {
        throw new Error("Failed to create sales order");
      }

      // Create sales order line items from quote line items
      const salesOrderLineItemsStore = this.rootStore
        .salesOrderLineItemsStore as ServiceStore;
      const quoteLineItems = this.quoteLineItems;

      for (const quoteItem of quoteLineItems) {
        const soLineItem: Record<string, unknown> = {
          salesOrderId:
            typeof createdSO.id === "string"
              ? parseInt(createdSO.id, 10)
              : createdSO.id,
          quoteLineItemId: quoteItem.id,
          lineNumber: quoteItem.lineNumber,
          itemType: quoteItem.itemType,
          productCode: quoteItem.productCode || "",
          description: quoteItem.description || "",
          longDescription: quoteItem.longDescription || "",
          specifications: quoteItem.specifications || "",
          orderedQuantity: quoteItem.quantity,
          unitPrice: quoteItem.unitPrice,
          discountPercent: quoteItem.discountPercent || 0,
          totalPrice: quoteItem.totalPrice,
          notes: quoteItem.notes || "",
          status: "Ordered",
        };

        // Only include panelId if it exists and is a number
        if (quoteItem.panelId && typeof quoteItem.panelId === "number") {
          soLineItem.panelId = quoteItem.panelId;
        }

        try {
          await salesOrderLineItemsStore.create(soLineItem);
        } catch (lineError) {
          console.error("Failed to create sales order line item:", lineError);
        }
      }

      // Generate panels from quote line items using panel service
      // Pass jobId as jobNumber since panels use jobNumber to relate to jobs
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const foundJob = jobStore?.entities?.find(
        (job: Job) => Number(job.id) === Number(quote.jobId)
      );
      console.log("Generating panels for job number:", foundJob);
      await this.generatePanelsFromLineItems(
        quoteLineItems,
        foundJob.jobNumber
      );

      runInAction(() => {
        this.activeSalesOrderId =
          typeof createdSO.id === "string"
            ? parseInt(createdSO.id, 10)
            : createdSO.id;
        this.currentStep = 2; // Move to next step
      });

      // Update quote status to ConvertedToSO
      try {
        await quotesStore.patch(quote.id, {
          status: "ConvertedToSO",
          convertedToSOId: createdSO.id,
          conversionDate: new Date().toISOString(),
        });
      } catch (patchError) {
        console.error("Failed to update quote status:", patchError);
      }

      this.rootStore.notificationStore?.addNotification({
        message: `Sales Order ${createdSO.soNumber} created successfully`,
        options: { variant: "success" },
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          (error as Error).message || "Failed to convert quote to sales order";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Generate panels from sales order line items
  private generatePanelsFromLineItems = async (
    lineItems: QuoteLineItemType[],
    jobNumber?: number
  ) => {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    let panelCount = 1;
    const actualJobNumber = jobNumber || 10; // Default job number if not provided

    for (const lineItem of lineItems) {
      // Generate panels based on quantity (limit to reasonable number for demo)
      const panelsToCreate = Math.min(lineItem.quantity, 5);

      for (let i = 0; i < panelsToCreate; i++) {
        // Generate random identifiers following the pattern from PurchaseOrderViewStore
        const randomControlNumber = Math.floor(Math.random() * 9000) + 1000;
        const seriesLetter = String.fromCharCode(
          65 + Math.floor(Math.random() * 26)
        );
        const seriesNumber = String(
          Math.floor(Math.random() * 99) + 1
        ).padStart(2, "0");
        const markSeries = `${seriesLetter}${seriesNumber}`;

        // Generate realistic dimensions
        const panelLength = Math.floor(Math.random() * 16) + 8; // 8-24 feet
        const panelHeight = Math.floor(Math.random() * 4) + 8; // 8-12 feet
        const panelThickness = Math.floor(Math.random() * 4) + 4; // 4-8 inches

        // Calculate realistic values
        const totalSqFt = panelLength * panelHeight + Math.random() * 10;
        const volume = (totalSqFt * (panelThickness / 12)) / 27; // Convert to cubic yards
        const assembledPanelWeight = totalSqFt * (120 + Math.random() * 30); // 120-150 lbs per sq ft

        // Production details
        const finishTypes = [
          "Smooth",
          "Textured",
          "Architectural",
          "Sandblasted",
          "Exposed Aggregate",
        ];
        const castingTypes = [
          "Standard",
          "Insulated",
          "Structural",
          "Architectural",
        ];
        const productTypes = ["PWP", "IWP", "SBP", "DFP", "SSB"];

        const panelData: Panel = {
          panelId: panelCount.toString().padStart(3, "0"),
          controlNumber: randomControlNumber.toString(),
          controlUUID: `${actualJobNumber}${randomControlNumber}`,
          markNumber: `M-${markSeries}-${i + 1}`,
          markUUID: `${actualJobNumber}M-${markSeries}-${i + 1}`,
          jobNumber: actualJobNumber,
          // Note: jobId removed - panels use jobNumber, not jobId

          // Dimensions and specifications
          panelLength: `${panelLength}'-${Math.floor(Math.random() * 12)}"`,
          totalSqFt: Math.round(totalSqFt * 10) / 10,
          volume: Math.round(volume * 100) / 100,
          assembledPanelWeight: Math.round(assembledPanelWeight),

          // Production details
          finishType:
            finishTypes[Math.floor(Math.random() * finishTypes.length)],
          castingType:
            castingTypes[Math.floor(Math.random() * castingTypes.length)],
          productType:
            productTypes[Math.floor(Math.random() * productTypes.length)],

          // Manufacturing details
          moldNumber: `M${Math.floor(Math.random() * 99) + 1}`,
          fabricationSequence: `${Math.floor(Math.random() * 100) + 1}`,
          installationSequence: `${Math.floor(Math.random() * 100) + 1}`,

          // Mix design
          mixDesign1: `MIX-${Math.floor(Math.random() * 9) + 1}`,
          mix1SqFt: Math.round(totalSqFt * (0.7 + Math.random() * 0.3)),
          mix1Volume:
            Math.round(volume * (0.7 + Math.random() * 0.3) * 100) / 100,

          // Additional properties
          veneerSqFt: Math.round(totalSqFt * (0.8 + Math.random() * 0.2)),
          netExposedSqFt: Math.round(totalSqFt * (0.6 + Math.random() * 0.3)),
          totalMixCubicYd: Math.round(volume * 100) / 100,
          individualPanelWeight: Math.round(assembledPanelWeight * 0.95),

          // Dates - castingDate will be set later in workflow
          createdDate: new Date().toISOString().split("T")[0],
          updatedDate: new Date().toISOString().split("T")[0],
        };

        try {
          // Create panel using ServiceStore
          const newPanel = await panelStore?.create?.(panelData);
          if (newPanel && newPanel.id) {
            // Track created panel ID for filtering
            this.createdPanelIds.push(newPanel.id as number);
            console.log(
              `Panel ${newPanel.panelId} created with ID ${newPanel.id}`
            );
          }
        } catch (error) {
          console.error("Failed to create panel:", error);
        }

        panelCount++;
      }
    }

    this.rootStore.notificationStore?.addNotification({
      message: `${panelCount - 1} panels generated for sales order`,
      options: { variant: "success" },
    });
  };

  updatePanelCastingDate = async (panelId: number, castingDate: string) => {
    try {
      const panelStore = this.rootStore.panelStore as ServiceStore;
      await panelStore?.patch?.(panelId, { castingDate });

      this.rootStore.notificationStore?.addNotification({
        message: `Panel casting date updated`,
        options: { variant: "success" },
      });
    } catch (error) {
      console.error("Failed to update panel casting date:", error);
    }
  };

  updatePanelTableAssignment = async (panelId: number, tableNumber: string) => {
    try {
      const panelStore = this.rootStore.panelStore as ServiceStore;
      await panelStore?.patch?.(panelId, { tableNumber });

      this.rootStore.notificationStore?.addNotification({
        message: `Panel assigned to Table ${tableNumber}`,
        options: { variant: "success" },
      });
    } catch (error) {
      console.error("Failed to update panel table assignment:", error);
    }
  };

  scheduleProduction = async (data: ProductionScheduleData) => {
    this.isLoading = true;

    try {
      // In production: create production order using productionOrdersStore
      console.log("Scheduling production:", data);

      // Mock success
      await new Promise((resolve) => setTimeout(resolve, 1000));

      runInAction(() => {
        this.currentStep = 4; // Move to complete
        this.scheduleDate = data.plannedStartDate;
      });

      this.rootStore.notificationStore?.addNotification({
        message: `Production scheduled for ${this.generatedPanels.length} panels`,
        options: { variant: "success" },
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          (error as Error).message || "Failed to schedule production";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Disposal method for proper cleanup
  dispose = () => {
    if (this.errorHandler) {
      this.errorHandler.dispose();
    }
  };
}
